// ─── Active address book — one file per chain, selected at build time ───────────────────────────
//
// Each chain's addresses live in ./deployments/<chainId>.js (deployLocal.cjs writes one per deploy).
// This used to be a single generated block right here, which meant mainnet, Sepolia and local
// hardhat all fought over ONE committed file: whoever deployed last won the working tree, git held a
// third version, and a branch rebuild could silently clobber a live site. Now the book is chosen by
// the VITE_TARGET_CHAIN build var, so every environment builds from the SAME branch and differs only
// by that one Vercel setting. Adding a chain = drop in ./deployments/<id>.js + one import + one BOOKS
// row below. See CHANGELOG entry 234.
import * as book1 from './deployments/1.js'
import * as book11155111 from './deployments/11155111.js'
import * as book31337 from './deployments/31337.js'

const BOOKS = { 1: book1, 11155111: book11155111, 31337: book31337 }

// Vite inlines import.meta.env.VITE_TARGET_CHAIN at build. A Node deploy script that require()s this
// module has no import.meta.env, so fall back to process.env; unset in either → 31337 (local), which
// is the historical committed default, so nothing changes until a project explicitly sets the var.
const TARGET_CHAIN = Number(
  (import.meta.env && import.meta.env.VITE_TARGET_CHAIN) ||
  (typeof process !== 'undefined' && process.env && process.env.VITE_TARGET_CHAIN) ||
  31337
)
const BOOK = BOOKS[TARGET_CHAIN] || BOOKS[31337]

export const IS_DEPLOYED = BOOK.IS_DEPLOYED;
export const CHAIN_ID = BOOK.CHAIN_ID;
export const ADDRESSES = BOOK.ADDRESSES;

// ─── Single-asset staking copy ─────────────────────────────────
// The VAULTS themselves are enumerated from StakingVaultFactory, so a
// newly listed asset appears with no code change. This map holds only
// the prose, keyed by the staked asset's address (lowercase).
//
// Deliberately copy-only. No rate, no APR, no cap - every number the
// page shows is read from the chain, so nothing here can drift from
// what the contracts actually pay. An asset with no entry simply
// renders without a description rather than being hidden.
export const STAKING_VAULT_COPY = {
  "0x58d7fa7356d68c494c2de09f95d55c2ff2d38bd3": {
    blurb: "Stake AURYN and earn HORIZON. One asset in, and your AURYN balance stays whole.",
  },
};

// ─── Protocol constants ────────────────────────────────────────
// Single source of truth for values shared between frontend +
// contracts.

export const SEC_PER_DAY    = 86_400;

// ─── Is this address book usable on the chain we are actually reading? ──────
//
// `IS_DEPLOYED` above means only "the generated block was populated". It says nothing about WHERE,
// and the two came apart the moment the site was served publicly: `CHAIN_ID` is 31337 (the local
// hardhat chain the addresses were generated against), while `wagmi-config.js` serves `[mainnet]`
// to every host that is not localhost. So the deployed site asked MAINNET for DEV-CHAIN addresses,
// where `eth_getCode` returns `0x` and no read can ever succeed.
//
// **The failure was invisible because every page degraded differently.** Vaults gates its list on
// `isLoading`, which never resolves, so it showed a loading skeleton forever. Markets and Multiply
// default their figures to zero, so they rendered a COMPLETE, plausible protocol - four assets,
// 0.00% APYs, a green "Healthy" bar and working-looking Supply/Borrow buttons. The skeleton was the
// honest one. A visitor could not tell "not deployed" from "deployed and unused", and the second
// reading is the damaging one.
//
// **The root cause is fixed in `wagmi-config.js`**, which now derives the chain it reads FROM
// `CHAIN_ID` instead of hardcoding mainnet - so a Sepolia or Base deploy reads Sepolia or Base with
// no further change, and a public TESTNET is a `deployLocal.cjs` target away.
//
// What remains, and what this flag is for, is the one case no RPC choice can rescue: an address
// book built for the LOCAL hardhat chain, served from a public host. 127.0.0.1 is not reachable
// from app.horizon.win, so those reads cannot succeed no matter which chain the config selects. It is
// structural rather than a network condition, which is why it is worth detecting rather than
// waiting to observe.
/// Pure so it can be tested. The constants below are one call each, because the condition this
/// guards against is by definition one the dev machine never sees - a predicate that can only be
/// exercised on the host where it matters is a predicate nobody checks.
export function isLocalHost(hostname) {
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

/// A local-chain address book is unusable anywhere except the machine running that chain.
export function chainMismatch(chainId, hostname) {
  return chainId === 31337 && !isLocalHost(hostname);
}

/// Whether the app may open a loopback RPC. Pure, and tested, because getting it wrong is not a
/// cosmetic bug: a publicly-served page that opens `http://127.0.0.1:8545` triggers Chrome's
/// local-network-access prompt on the VISITOR's machine ("wants to access other apps and services
/// on this device"), which is indistinguishable from a site probing their LAN. Shipped exactly once,
/// on 2026-08-10, when deriving the chain from `CHAIN_ID` selected hardhat on a public host.
export function usesLoopback(chainId, hostname) {
  return chainId === 31337 && isLocalHost(hostname);
}

const HOSTNAME = typeof window !== 'undefined' ? window.location.hostname : 'localhost';

export const IS_LOCAL_HOST = isLocalHost(HOSTNAME);
export const CHAIN_MISMATCH = chainMismatch(CHAIN_ID, HOSTNAME);

/// What every read gate should ask, in place of `IS_DEPLOYED`: are these addresses populated AND
/// reachable from here? One named concept, so a page cannot answer it differently by accident.
export const IS_LIVE = IS_DEPLOYED && !CHAIN_MISMATCH;

/// The canonical zero address, exported once so ~28 files stop each re-declaring it (and can't drift
/// on casing). `isLiveAddr(addr)` is the read gate every hook re-derives: live chain AND a real,
/// non-zero address for the thing being read - see `IS_LIVE`'s own note on answering it one way.
export const ZERO_ADDR = '0x0000000000000000000000000000000000000000';
export const isLiveAddr = (a) => IS_LIVE && !!a && a.toLowerCase() !== ZERO_ADDR;

// ─── Chain + explorer metadata ────────────────────────────────
// Used by the TopBar chain badge and the footer explorer links.
// Hardhat/anvil localnet (CHAIN_ID === 31337) gets a "Local" label
// and no explorer URL; any other ID tries to look up a name/explorer
// from this map, falling back to a generic "Chain #<id>" label.
// `native` is the chain's gas coin symbol - what a user sees when depositing the native asset into
// a vault via NativeVaultZap (ETH on Ethereum/Base/Arbitrum/Optimism, POL on Polygon, S on Sonic).
// It is the label only; the wrapped-native ERC20 the zap actually routes through is ADDRESSES.WETH
// on the active chain. Kept here so the multichain answer is one table, not a scattered set of
// per-component conditionals.
// `testnet: true` is what a chain-picker/badge reads to append a "Testnet" marker; production chains
// simply omit it (undefined === falsy). Sepolia is the ONE public testnet this app builds against
// (the pre-launch site, VITE_TARGET_CHAIN=11155111); 31337 is the local dev chain and stays labelled
// "Local" rather than "Testnet". Added so the Sepolia build names itself "Sepolia" with real Etherscan
// links instead of falling back to "Chain #11155111" with none.
const CHAIN_META = {
  1:       { name: 'Ethereum',   explorer: 'https://etherscan.io',                  native: 'ETH' },
  11155111:{ name: 'Sepolia',    explorer: 'https://sepolia.etherscan.io',          native: 'ETH', testnet: true },
  8453:    { name: 'Base',       explorer: 'https://basescan.org',                  native: 'ETH' },
  42161:   { name: 'Arbitrum',   explorer: 'https://arbiscan.io',                   native: 'ETH' },
  10:      { name: 'Optimism',   explorer: 'https://optimistic.etherscan.io',       native: 'ETH' },
  137:     { name: 'Polygon',    explorer: 'https://polygonscan.com',               native: 'POL' },
  31337:   { name: 'Local',      explorer: null,                                    native: 'ETH' },
}
export function chainMeta(id = CHAIN_ID) {
  return CHAIN_META[id] ?? { name: `Chain #${id}`, explorer: null, native: 'ETH' }
}
/// The chain's native gas-coin symbol (ETH, POL, S, ...) - the label for the native vault-deposit
/// option. Falls back to ETH for an unmapped chain rather than showing a blank.
export function nativeCoinSymbol(id = CHAIN_ID) {
  return chainMeta(id).native ?? 'ETH'
}
export function addressLink(addr, id = CHAIN_ID) {
  const m = chainMeta(id)
  return m.explorer ? `${m.explorer}/address/${addr}` : null
}
export function txLink(hash, id = CHAIN_ID) {
  const m = chainMeta(id)
  return m.explorer ? `${m.explorer}/tx/${hash}` : null
}

